/**
 * Simple view (#476) — the shared pieces every placement uses: the account
 * setting hook, the toast, the top switch, the Simple · Everything segmented
 * switch, the Account toggle row, and the plain money helpers.
 *
 * The setting lives on the account (`view_mode` "full" | "simple",
 * `simple_guide_seen`), so every surface follows it on its next open or sync;
 * the last write wins. Nothing here ever switches view on its own.
 *
 * Author: Maré Greyling (Thevertex) <mare.greyling@gmail.com> — https://mare-greyling.com
 */
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../../api.js';
import { useAuth } from '../../App.jsx';
import { fmtR, fmtWhole } from '../../money.js';
import './simple.css';

const TOAST_EVENT = 'vault:view-toast';

/** Show one plain toast at the top of the page (host: ViewToastHost). */
export function showViewToast(message) {
  window.dispatchEvent(new CustomEvent(TOAST_EVENT, { detail: { message } }));
}

/** App-wide toast host — survives the full ↔ Simple remount. */
export function ViewToastHost() {
  const [msg, setMsg] = useState('');
  useEffect(() => {
    let t;
    const on = (e) => {
      setMsg(e.detail?.message || '');
      clearTimeout(t);
      t = setTimeout(() => setMsg(''), 3200);
    };
    window.addEventListener(TOAST_EVENT, on);
    return () => { window.removeEventListener(TOAST_EVENT, on); clearTimeout(t); };
  }, []);
  if (!msg) return null;
  return <div className="sv-toast" role="status" aria-live="polite">{msg}</div>;
}

/** Keep the device cache in step so the right view opens offline. */
function cacheMe(m) {
  try { localStorage.setItem('vault_last_me', JSON.stringify(m)); } catch { /* private mode */ }
}

/**
 * The account's view setting. `setMode` writes it (optimistic, rolled back on
 * failure) and can raise a toast and run a follow-up once the new view has
 * rendered; `markGuideSeen` stamps the guide flag so it never repeats.
 */
export function useViewMode() {
  const { me, setMe } = useAuth() || {};
  const simple = me?.view_mode === 'simple';

  const patch = async (body) => {
    const r = await api('/auth/me/', { method: 'PATCH', body });
    setMe((m) => { const n = { ...m, ...r }; cacheMe(n); return n; });
    return r;
  };

  const setMode = async (mode, { toast, then } = {}) => {
    const was = me?.view_mode || 'full';
    setMe((m) => ({ ...m, view_mode: mode }));
    try {
      await patch({ view_mode: mode });
      if (toast) showViewToast(toast);
      if (then) setTimeout(then, 350);   // after the other view has mounted
      return true;
    } catch {
      setMe((m) => ({ ...m, view_mode: was }));
      showViewToast('Could not switch — check your connection and try again.');
      return false;
    }
  };

  const markGuideSeen = async () => {
    setMe((m) => ({ ...m, simple_guide_seen: true }));
    try { await patch({ simple_guide_seen: true }); } catch { /* retried next time the guide closes */ }
  };

  return { simple, guideSeen: !!me?.simple_guide_seen, setMode, markGuideSeen };
}

/* Plain money in the account's currency (money.js): whole units where the
   figure is whole, cents only when real. The names stay "rand" for history. */
export const randWhole = (v) => fmtWhole(Math.round(Number(v) || 0));
export const rand = (v) => {
  const n = Number(v) || 0;
  return Math.abs(n - Math.round(n)) < 0.005 ? fmtWhole(Math.round(n)) : fmtR(n);
};
export const randCents = (v) => fmtR(Number(v) || 0);

export const ordinal = (n) => {
  const t = n % 100;
  if (t >= 11 && t <= 13) return `${n}th`;
  return n + ({ 1: 'st', 2: 'nd', 3: 'rd' }[n % 10] || 'th');
};

/**
 * Safe to spend today = the summary's leftover_actual over the days to payday,
 * whole rand. Same rule the phone Home and the watch use (payday =
 * settings.cycle_start_day; the savings pot is never subtracted — #666).
 */
export function safeToSpend(summary, paydayDay, now = new Date()) {
  const left = Math.max(0, Number(summary?.leftover_actual ?? summary?.leftover ?? 0));
  const payday = Number(paydayDay) || 25;
  const dom = now.getDate();
  const dim = new Date(now.getFullYear(), now.getMonth() + 1, 0).getDate();
  const days = Math.max(1, dom < payday ? payday - dom : (dim - dom) + payday);
  return { left, payday, days, perDay: Math.max(0, Math.round(left / days)) };
}

/* Small inline icons (the web app has no icon font). */
export const Icon = {
  check: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M5 12.5l4.5 4.5L19 7.5" /></svg>,
  plus: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true"><path d="M12 5v14M5 12h14" /></svg>,
  rows: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" aria-hidden="true"><rect x="4" y="5" width="16" height="5" rx="1.5" /><rect x="4" y="14" width="16" height="5" rx="1.5" /></svg>,
  grid: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true"><rect x="4" y="4" width="6.5" height="6.5" rx="1.2" /><rect x="13.5" y="4" width="6.5" height="6.5" rx="1.2" /><rect x="4" y="13.5" width="6.5" height="6.5" rx="1.2" /><rect x="13.5" y="13.5" width="6.5" height="6.5" rx="1.2" /></svg>,
  camera: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" aria-hidden="true"><path d="M4 8h3l1.5-2.5h7L17 8h3v11H4z" /><circle cx="12" cy="13" r="3.5" /></svg>,
  keyboard: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" aria-hidden="true"><rect x="3" y="6" width="18" height="12" rx="2" /><path d="M7 10h.01M11 10h.01M15 10h.01M17 14H7" /></svg>,
  mic: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" aria-hidden="true"><rect x="9" y="3" width="6" height="11" rx="3" /><path d="M5.5 11a6.5 6.5 0 0013 0M12 17.5V21" /></svg>,
  back: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M14.5 6l-6 6 6 6" /></svg>,
  undo: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M5 5v5h5" /><path d="M5.5 10A7 7 0 1112 19" /></svg>,
  spinner: <svg className="sv-spin" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" aria-hidden="true"><path d="M12 3a9 9 0 109 9" /></svg>,
};

/**
 * The top switch. In full view it reads "Simple view" and turns Simple view on;
 * in Simple view it reads "Show everything" and turns it off. `backToSimple`
 * is for a full screen reached by a deep link while Simple view is on: it
 * takes the member back to Simple Home without changing the setting.
 */
export function TopViewSwitch({ backToSimple = false, dataSv }) {
  const { simple, setMode } = useViewMode();
  const nav = useNavigate();
  if (backToSimple) {
    return (
      <button type="button" className="sv-pill" onClick={() => nav('/app')} data-sv={dataSv}>
        {Icon.rows}Simple view
      </button>
    );
  }
  const go = () => (simple
    ? setMode('full', { toast: 'Showing everything. Switch back with “Simple view”.' })
    : setMode('simple'));
  return (
    <button type="button" className="sv-pill" onClick={go} data-sv={dataSv}>
      {simple ? Icon.grid : Icon.rows}{simple ? 'Show everything' : 'Simple view'}
    </button>
  );
}

/**
 * Simple · Everything at the top of the This month list, in BOTH views.
 * Simple keeps the member on Budget; Everything opens the full Budget list.
 */
export function BudgetViewSwitch({ id }) {
  const { simple, setMode } = useViewMode();
  const nav = useNavigate();
  const pick = (toSimple) => {
    if (toSimple === simple) return;
    if (toSimple) {
      nav('/app?s=budget');   // land on Budget, not Home
      setMode('simple');
    } else {
      setMode('full', {
        toast: 'Showing everything. Switch back with “Simple”.',
        then: () => document.getElementById('sv-budget-switch')?.scrollIntoView({ block: 'start' }),
      });
    }
  };
  return (
    <div className="sv-seg" role="radiogroup" aria-label="How Vault looks" id={id}>
      {[[true, 'Simple'], [false, 'Everything']].map(([v, label]) => (
        <button key={label} type="button" role="radio" aria-checked={simple === v ? 'true' : 'false'}
                onClick={() => pick(v)}>{label}</button>
      ))}
    </div>
  );
}

/** Account → first row: the Simple view switch with its plain note. */
export function SimpleViewSetting() {
  const { simple, setMode } = useViewMode();
  return (
    <>
      <div className="sv-setting">
        <div style={{ flex: 1, minWidth: 0 }}>
          <div className="sv-setting-title">Simple view</div>
          <div className="sv-setting-line">
            Shows only what you can spend, logging a spend and what’s paid. Everything else stays one tap away.
          </div>
        </div>
        <button type="button" role="switch" aria-checked={simple ? 'true' : 'false'} aria-label="Simple view"
                className="sv-switch" onClick={() => setMode(simple ? 'full' : 'simple')}>
          <span className="sv-switch-track"><span className="sv-switch-knob" /></span>
        </button>
      </div>
      <div className="sv-setting-note" aria-live="polite">
        {simple
          ? 'On. Web, phone, iPad and your watch open in Simple view.'
          : 'Off. Vault shows everything — the full view.'}
      </div>
    </>
  );
}
